/**
 * Ekran biçimlendirme yardımcıları (F1·S10). İş kuralı İÇERMEZ — kurallar
 * @isg/validation'da; burada yalnız görsel sunum.
 *
 * Kimlik maskesi ARTIK BURADA DEĞİL (madde 37 ✅ 05.08.2026): maske API
 * kenarında uygulanır (`@isg/validation maskeliKimlikNo` — tek nokta), ekran
 * maskeli alanı (`kimlikNoMaskeli`) olduğu gibi gösterir; tam değer yalnız
 * kimlik-full ucundan (gösterim loglu) gelir.
 *
 * ✏️ **GÖRÜNÜM ARTÇISI (G11, 27.08.2026) — TARİH BİÇİMİ TEK KAYNAKTAN.**
 *
 * > Renk için ne dediysek tarih için de aynısı geçerli: **görünen değer tek
 * > yerden okunur.** Ürün sahibi canlı kullanımda en az iki biçim gördü
 * > (`2026-09-26` aksiyon listesinde · `07.10.2024` kişi kartında) — ikisi de
 * > "doğru" görünüyordu, çünkü her ekran kendi biçimini yazıyordu.
 *
 * TR konvansiyonu **GG.AA.YYYY**'dir ve bu dosyanın dışında hiçbir ekran
 * tarih biçimlendirmez (`toLocale*` · `Intl.DateTimeFormat` · `slice(0, 10)`
 * kod denetimiyle yasak — `e2e/tarih-bicimi.spec.ts`, sabit renk taramasının
 * kardeşi).
 *
 * ⚠️ **SINIR: YALNIZ KULLANICIYA GÖRÜNEN BİÇİM.** API istek/yanıt gövdeleri ·
 * veri · CSV · **İBYS payload'ı** ISO kalır (İBYS düzeni Bakanlık kararıdır —
 * Kapsam §16.11/J). Buradaki fonksiyonlar bir **sunum** katmanıdır; bir
 * isteğin gövdesine bu dosyadan çıkan metin YAZILMAZ.
 */

/** ISO gün parçası (YYYY-AA-GG) — tam ISO damgası da kabul edilir. */
function isoGunParcasi(iso: string): string {
  return iso.slice(0, 10);
}

/**
 * ISO tarihi TR görünümüne çevirir (**GG.AA.YYYY**); null → uzun tire.
 *
 * ⚠️ Tam ISO damgası (`2026-09-26T00:00:00.000Z`) verilirse **gün parçası**
 * okunur — saat dilimine çevrilmez. Gerekçe: gün hassasiyetli alanlar
 * (`termin_tarihi`, `olay_tarihi`) UTC gece yarısında saklanır; yerel saate
 * çevirmek Türkiye'de **bir gün geri** kaydırırdı.
 */
export function tarihGoster(iso: string | null | undefined): string {
  if (!iso) return '—';
  const [y, a, g] = isoGunParcasi(iso).split('-');
  if (!y || !a || !g) return iso;
  return `${g}.${a}.${y}`;
}

/**
 * ISO damgasını TR görünümüne çevirir (**GG.AA.YYYY SS:DD**, YEREL saat);
 * null → uzun tire.
 *
 * ⚠️ **AN'I GÖSTEREN ALANLAR YEREL SAATE ÇEVRİLİR** (imza · geçiş · tarama),
 * gün hassasiyetli alanların aksine — bir imzanın "saat kaçta atıldığı"
 * kullanıcının saatidir. `toLocaleString` yerine elle kurulur ki çıktı ICU
 * sürümünden bağımsız olsun (ve saniye taşımasın).
 */
export function zamanGoster(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const iki = (v: number): string => String(v).padStart(2, '0');
  return `${iki(d.getDate())}.${iki(d.getMonth() + 1)}.${d.getFullYear()} ${iki(d.getHours())}:${iki(d.getMinutes())}`;
}

/**
 * **DÖNEM KİMLİĞİ TARİH DEĞİLDİR — ama tarih taşır.** `donemKimligi`
 * (@isg/validation) biçime göre üç şekil üretir ve üçü de ekrana çıkar:
 *
 *   `2026`        → `2026`        (takvim yılı — çevrilecek bir şey yok)
 *   `2026-03`     → `03.2026`     (takvim ayı)
 *   `2026-03-14`  → `14.03.2026`  (kayan biçim — dönemin kimliği VADEDİR)
 *
 * Aynı listede `2026-03` ile `14.03.2026` yan yana durduğu için biçim
 * dönüşümü **burada** yapılır; ekran hangi şeklin geldiğini bilmez.
 */
export function donemGoster(donem: string | null | undefined): string {
  if (!donem) return '—';
  const parca = donem.split('-');
  if (parca.length === 1) return donem;
  if (parca.length === 2) return `${parca[1]}.${parca[0]}`;
  return tarihGoster(donem);
}

/**
 * AY görünümü (**AA.YYYY**) — girdi ISO gün ya da tam damga olabilir.
 * Çalışma saati dönemi şemada bir **DATE**tir (ayın ilk günü); ekranda gün
 * gösterilmez, çünkü kayıt **aya** aittir. `donemGoster`den ayrı durur: o üç
 * ŞEKLİ ayırt eder, bu tek şekli AYA indirir.
 */
export function ayGoster(iso: string | null | undefined): string {
  if (!iso) return '—';
  const [y, a] = isoGunParcasi(iso).split('-');
  if (!y || !a) return iso;
  return `${a}.${y}`;
}

/**
 * ⚠️ **BU BİR GÖRÜNÜM BİÇİMİ DEĞİL, BİR FORM DEĞERİDİR.** `<input
 * type="datetime-local">` `YYYY-AA-GGTSS:DD` bekler ve **yerel saat** okur;
 * değer buradan üretilir, ISO'ya çevrim GÖNDERİMDE yapılır. Kullanıcıya
 * görünen metin tarayıcının kendi yerelleştirmesidir — bizim biçimimiz değil.
 * Burada durmasının sebebi tek: aynı dönüşümü iki ekranın ayrı ayrı yazması.
 */
export function datetimeLocalDegeri(tarih: Date): string {
  const ofsetli = new Date(tarih.getTime() - tarih.getTimezoneOffset() * 60_000);
  return ofsetli.toISOString().slice(0, 16);
}

/** ISO tarihe gün ekler (saat dilimi sürprizsiz — UTC üzerinden). */
export function gunEkle(isoDate: string, gun: number): string {
  const d = new Date(`${isoDate}T00:00:00.000Z`);
  d.setUTCDate(d.getUTCDate() + gun);
  return isoGunParcasi(d.toISOString());
}

/**
 * GÜN GİRDİSİ → ISO DAMGASI (F4·B2 · §2.40 — *yalan söyleyen kontrol*).
 *
 * > **Kapatılan arıza:** olay bildiriminde tarih düz bir metin kutusuydu ve
 * > `2026-08-2026` yazılabiliyordu. Gönderim anında
 * > `new Date('2026-08-2026T00:00:00.000Z').toISOString()` bir `RangeError`
 * > fırlatıyor, hata `onClick` içinde patladığı için **istek hiç
 * > kurulmuyordu**: kullanıcı *"Bildir"*e basıyor ve **hiçbir şey
 * > olmuyordu.** Kontrol görünüyordu ama çalışmıyordu.
 *
 * Bu fonksiyon `tarihGoster`in tersidir ve **tek kaynak** olması aynı
 * gerekçeyle: girdiyi ayrıştıran ikinci bir yer, ilkinden farklı bir şeyi
 * "geçerli" sayar.
 *
 * Geçersiz girdide **null döner, ATMAZ** — çağıran ekran görünür bir uyarı
 * basar ve düğmeyi kapatır (yalan söyleyen kontrolün doğru çözümü: kontrol
 * ya çalışır ya kapalıdır; basılınca hata veren üçüncü seçenek en kötüsü).
 *
 * ⚠️ **TAKVİM AY UZUNLUĞUNU DOĞRULAR:** `2026-02-30` biçim olarak
 * kusursuzdur ama bir gün değildir — `Date` onu 2 Mart'a **sessizce
 * kaydırırdı**. Geri okuma karşılaştırması bunu yakalar.
 */
export function gunGirdisiIso(deger: string): string | null {
  const kirpik = deger.trim();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(kirpik)) return null;
  const damga = new Date(`${kirpik}T00:00:00.000Z`);
  if (Number.isNaN(damga.getTime())) return null;
  // Takvim denetimi: `Date` 2026-02-30'u 2026-03-02'ye kaydırır; geri
  // okunan gün girdiyle aynı değilse böyle bir gün YOKTUR.
  if (isoGunParcasi(damga.toISOString()) !== kirpik) return null;
  return damga.toISOString();
}

/**
 * ⚠️ **"BUGÜN" BU DOSYADA YOKTUR (madde 302, 08.10.2026).** `bugunIso` ve
 * `bugunGunGirdisi` tarayıcı saatinden gün üretiyordu — ikinci bir doğruluk
 * kaynağı. Tarih alanlarının varsayılanı ve `max` sınırı SAHA GÜNÜNDEN
 * (sunucu — `useSahaGunu` · `lib/saha-gunu.ts useVarsayilanGun`) okunur;
 * ekran `new Date()`ten gün üretmez (kanarya `vade-tek-yuklem-envanteri.spec`).
 */

/**
 * KAYITLI DEĞERİN GÜN GİRDİSİ (`YYYY-AA-GG`) — takvim alanına geri yazılırken
 * (tur 6 künye · evrak vade). `gunGirdisiIso`nun tersi; saat dilimine
 * ÇEVRİLMEZ (gün hassasiyetli alan), ekran `slice(0, 10)` yazmaz (G11).
 */
export function gunGirdisiDegeri(iso: string | null | undefined): string {
  return iso ? isoGunParcasi(iso) : '';
}

/**
 * ═ SAYI BİÇİMİ — TEK KAYNAK (TUR 12; G11'in sayı kardeşi) ═════════════════
 *
 * ⚠️ **BU BÖLÜM BİR YOL TESTİ BULGUSUYLA DOĞDU.** Dashboard oranları önce
 * ekranda `toLocaleString('tr-TR', …)` ile basılıyordu ve **G11 kod
 * denetimi haklı olarak kırmızı yandı**: kural *"kullanıcıya görünen biçim
 * tek yerden okunur"* der ve tarihle sınırlı olmasının bir gerekçesi yoktur.
 * Ondalık ayracı da en az tarih kadar sessizce ayrışır — bir ekran `4.32`,
 * öteki `4,32` yazar ve ikisi de "doğru" görünür.
 *
 * ⚠️ **BOŞ ≠ SIFIR (Kapsam §26.5):** `null` girdi **uzun tire** basar,
 * `0` basmaz. Sıfır bir ÖLÇÜMDÜR (*"bu dönemde kaza olmadı"*), boş bir
 * BİLGİSİZLİKTİR (*"paydayı bilmiyoruz"*).
 */

/** Ondalıklı sayı (TR ayracı). `null` → uzun tire (BOŞ ≠ SIFIR). */
export function sayiGoster(deger: number | null | undefined, basamak = 1): string {
  if (deger === null || deger === undefined) return '—';
  return deger.toLocaleString('tr-TR', {
    minimumFractionDigits: 0,
    maximumFractionDigits: basamak,
  });
}

/** Oran hücresi — iki ondalık SABİT (tablo kolonu hizalı okunsun). */
export function oranGoster(deger: number | null | undefined): string {
  if (deger === null || deger === undefined) return '—';
  return deger.toLocaleString('tr-TR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

/** Tam sayı (binlik ayraçlı) — çalışma saati gibi büyük sayaçlar için. */
export function tamSayiGoster(deger: number | null | undefined): string {
  if (deger === null || deger === undefined) return '—';
  return deger.toLocaleString('tr-TR', { maximumFractionDigits: 0 });
}
